import { z } from 'zod/v4'
import type { StructuredExpression } from '../../../../../resources/filterTypes'

export const KanbanGroupKeySchema = z.object({
  field: z.string(),
  groupingKey: z.string(),
})

export type KanbanGroupKey = z.infer<typeof KanbanGroupKeySchema>

export const DEFAULT_KANBAN_GROUP: KanbanGroupKey = {
  field: '',
  groupingKey: '',
}

/** Column title for unset enum/select values when Section by options. */
export const UNDEFINED_KANBAN_GROUPING_KEY = 'N/A'

/**
 * Neutral grey accent for the N/A column in both light and dark themes
 * (theme `muted-foreground`, not a colored option swatch).
 */
export const UNDEFINED_KANBAN_GROUP_COLOR = 'hsl(var(--muted-foreground))'

/**
 * Remainder-column filter for enum/select boards: anything that is not a
 * known option value (missing Record row, empty data, or a stale/unknown
 * stored value). Falls back to isEmpty when no option values are supplied.
 */
export const buildUndefinedKanbanSubQuery = (
  field: string,
  optionValues: readonly string[] = [],
): StructuredExpression => {
  const values = [
    ...new Set(optionValues.filter((value) => value.trim().length > 0)),
  ]
  if (values.length === 0) {
    return { [field]: { isEmpty: true } }
  }
  if (values.length === 1) {
    return { NOT: { [field]: { eq: values[0] } } }
  }
  return {
    NOT: {
      OR: values.map((value) => ({ [field]: { eq: value } })),
    },
  }
}

export const isUndefinedKanbanGroupingKey = (groupingKey: string): boolean =>
  groupingKey === UNDEFINED_KANBAN_GROUPING_KEY

export type KanbanPartitionKey = {
  blockId: string
  section: KanbanGroupKey
  subsection: KanbanGroupKey
}

export type KanbanSectionScope = {
  blockId: string
  group: KanbanGroupKey
}

export type KanbanSubsectionScope = {
  blockId: string
  section: KanbanGroupKey
  subsection: KanbanGroupKey
}

export const isDefaultKanbanGroup = (group: KanbanGroupKey): boolean =>
  !group.field && !group.groupingKey

export const kanbanGroupKeysEqual = (
  left: KanbanGroupKey,
  right: KanbanGroupKey,
): boolean =>
  left.field === right.field && left.groupingKey === right.groupingKey

export const toKanbanGroupKey = (args: {
  field: string
  groupingKey: string
}): KanbanGroupKey => ({
  field: args.field,
  groupingKey: args.groupingKey,
})

export const buildKanbanSectionGroup = (
  hasSections: boolean,
  section: KanbanGroupKey,
): KanbanGroupKey => (hasSections ? section : DEFAULT_KANBAN_GROUP)

export const buildKanbanSubsectionGroup = (
  subsection: KanbanGroupKey,
): KanbanGroupKey =>
  isDefaultKanbanGroup(subsection) ? DEFAULT_KANBAN_GROUP : subsection

export const buildKanbanPartitionKey = (args: {
  blockId: string
  hasSections: boolean
  section: KanbanGroupKey
  subsection: KanbanGroupKey
}): KanbanPartitionKey => ({
  blockId: args.blockId,
  section: buildKanbanSectionGroup(args.hasSections, args.section),
  subsection: buildKanbanSubsectionGroup(args.subsection),
})

export const buildKanbanSectionScope = (args: {
  blockId: string
  hasSections: boolean
  section: KanbanGroupKey
}): KanbanSectionScope => ({
  blockId: args.blockId,
  group: buildKanbanSectionGroup(args.hasSections, args.section),
})

export const buildKanbanSubsectionScope = (args: {
  blockId: string
  hasSections: boolean
  section: KanbanGroupKey
  subsection: KanbanGroupKey
}): KanbanSubsectionScope => {
  const partition = buildKanbanPartitionKey(args)
  return {
    blockId: partition.blockId,
    section: partition.section,
    subsection: partition.subsection,
  }
}

export const serializeKanbanGroupKey = (group: KanbanGroupKey): string =>
  JSON.stringify({ field: group.field, groupingKey: group.groupingKey })

export const serializeKanbanPartitionKey = (key: KanbanPartitionKey): string =>
  JSON.stringify(key)

export const serializeKanbanSectionScope = (scope: KanbanSectionScope): string =>
  JSON.stringify(scope)

export const serializeKanbanSubsectionScope = (
  scope: KanbanSubsectionScope,
): string => JSON.stringify(scope)

export const parseKanbanSectionScope = (
  value: string,
): KanbanSectionScope | null => {
  try {
    const parsed = JSON.parse(value) as KanbanSectionScope
    if (
      parsed &&
      typeof parsed.blockId === 'string' &&
      parsed.group &&
      typeof parsed.group.field === 'string' &&
      typeof parsed.group.groupingKey === 'string'
    ) {
      return parsed
    }
  } catch {
    return null
  }
  return null
}

export const parseKanbanSubsectionScope = (
  value: string,
): KanbanSubsectionScope | null => {
  try {
    const parsed = JSON.parse(value) as KanbanSubsectionScope
    if (
      parsed &&
      typeof parsed.blockId === 'string' &&
      parsed.section &&
      parsed.subsection &&
      typeof parsed.section.field === 'string' &&
      typeof parsed.section.groupingKey === 'string' &&
      typeof parsed.subsection.field === 'string' &&
      typeof parsed.subsection.groupingKey === 'string'
    ) {
      return parsed
    }
  } catch {
    return null
  }
  return null
}

export const kanbanPartitionKeysEqual = (
  left: KanbanPartitionKey,
  right: KanbanPartitionKey,
): boolean =>
  left.blockId === right.blockId &&
  kanbanGroupKeysEqual(left.section, right.section) &&
  kanbanGroupKeysEqual(left.subsection, right.subsection)
